// Auth context — Clerk-backed.
//
// Phase 3 migration note: this file's *interface* is unchanged from the
// Supabase-Auth version that preceded it. `useAuth()` still returns
// { user, loading, error, signInWithGoogle, signOut, isAuthenticated }
// so every consuming component keeps working without edits. The provider
// just sources its values from Clerk hooks now.
//
// The actual <ClerkProvider> lives one level up in main.jsx — we depend
// on Clerk's context being in scope here.

import { createContext, useContext, useEffect, useState } from 'react';
import { useUser, useClerk, useSignIn } from '@clerk/clerk-react';
import { setSentryUser } from '../lib/sentry';

const AuthContext = createContext(null);

// Mirrors lib/server-auth.js. Frontend check is defense-in-depth — the
// backend re-validates on every API call, and Clerk's dashboard
// allowlist is the actual access boundary.
const ALLOWED_DOMAINS = ['bullogicwealth.com', 'bullogictax.com'];
const ALLOWED_EMAILS = [];

function isEmailAllowed(email) {
  if (!email) return false;
  const lower = email.toLowerCase();
  const domain = lower.split('@')[1];
  if (ALLOWED_DOMAINS.includes(domain)) return true;
  return ALLOWED_EMAILS.some(a => lower === a.toLowerCase());
}

export function AuthProvider({ children }) {
  const { isLoaded: userLoaded, isSignedIn, user: clerkUser } = useUser();
  const { signOut: clerkSignOut } = useClerk();
  const { signIn, isLoaded: signInLoaded } = useSignIn();
  const [error, setError] = useState(null);

  // Sync state to a domain-allowlist check. Clerk's own allowlist (set
  // in the dashboard during onboarding) is the primary gate; this check
  // is a belt-and-suspenders safeguard for the unlikely case where the
  // dashboard config is loosened by accident.
  const email = clerkUser?.primaryEmailAddress?.emailAddress || '';
  const allowed = isSignedIn && isEmailAllowed(email);

  // If a signed-in user fails the allowlist (shouldn't happen under
  // normal Clerk config but we still guard), sign them out and surface
  // a message.
  useEffect(() => {
    if (userLoaded && isSignedIn && !allowed) {
      setError('Access denied. Your email is not authorized to use this application.');
      clerkSignOut();
    }
  }, [userLoaded, isSignedIn, allowed, clerkSignOut]);

  // Push the current user to Sentry so error reports get tagged with
  // who hit them. Clears on sign-out so post-logout errors aren't
  // attributed to the previous user.
  useEffect(() => {
    if (allowed && clerkUser?.id) {
      setSentryUser({ id: clerkUser.id, email });
    } else {
      setSentryUser(null);
    }
  }, [allowed, clerkUser?.id, email]);

  // Kick off Google OAuth via Clerk. After Google returns, Clerk drops
  // the user at /sso-callback (handled in main.jsx) which finishes the
  // sign-in and redirects to /.
  async function signInWithGoogle() {
    setError(null);
    if (!signInLoaded) return;
    try {
      await signIn.authenticateWithRedirect({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/sso-callback`,
        redirectUrlComplete: window.location.origin,
      });
    } catch (e) {
      setError(e.errors?.[0]?.message || e.message || 'Sign-in failed');
    }
  }

  async function signOut() {
    await clerkSignOut();
  }

  // Shape preserved exactly so existing consumers (AuthGate, LoginPage,
  // any future component) don't need to change. `user` is a small
  // normalized object — components only ever read `user.email`.
  const value = {
    user: allowed ? { id: clerkUser.id, email } : null,
    loading: !userLoaded || !signInLoaded,
    error,
    signInWithGoogle,
    signOut,
    isAuthenticated: !!allowed,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
